import { useState, useEffect } from 'react'
import { 
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CogIcon,
  UsersIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { useToast } from '../ToastContainer'
import { getDefaultGroup, setDefaultGroup } from '../../services/api/admin'

interface Role {
  id: string
  name: string
  description?: string
  color: string
  rolePermissions?: Array<{
    permission: string
  }>
}

interface Group {
  id: string
  name: string
  description?: string
  color: string
  createdAt: string
  updatedAt: string
  isSystem?: boolean
  groupRoles: Array<{
    id: string
    role: {
      id: string
      name: string
      description?: string
      color: string
      rolePermissions: Array<{
        permission: string
      }>
    }
  }>
  users: Array<{
    id: string
    user: {
      id: string
      name: string
      email: string
      avatar?: string
    }
  }>
  _count: {
    users: number
  }
}

interface User {
  id: string
  name: string
  email: string
  avatar?: string
  role: string
  enabled: boolean
}

interface CreateGroupForm {
  name: string
  description: string
  color: string
  roleIds: string[]
}

interface GroupManagementState {
  groups: Group[]
  allUsers: User[]
  availableRoles: Role[]
  loading: boolean
  error: string | null
  showCreateModal: boolean
  showEditModal: boolean
  showDeleteModal: boolean
  showAssignUsersModal: boolean
  editingGroup: Group | null
  deletingGroupId: string | null
  assigningToGroupId: string | null
  defaultGroup: Group | null
  defaultGroupLoading: boolean
  savingDefaultGroup: boolean
}

const GroupManagement = () => {
  const { showSuccess, showError } = useToast()
  
  const [state, setState] = useState<GroupManagementState>({
    groups: [],
    allUsers: [],
    availableRoles: [],
    loading: true,
    error: null,
    showCreateModal: false,
    showEditModal: false,
    showDeleteModal: false,
    showAssignUsersModal: false,
    editingGroup: null,
    deletingGroupId: null,
    assigningToGroupId: null,
    defaultGroup: null,
    defaultGroupLoading: false,
    savingDefaultGroup: false
  })

  const [createForm, setCreateForm] = useState<CreateGroupForm>({
    name: '',
    description: '',
    color: '#6b7280',
    roleIds: []
  })

  useEffect(() => {
    Promise.all([
      fetchGroups(),
      fetchUsers(),
      fetchAvailableRoles(),
      fetchDefaultGroup()
    ])
  }, [])

  const fetchGroups = async () => {
    try {
      const response = await fetch('/api/groups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取群組失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, groups: data.data.groups || [] }))
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message }))
    }
  }

  // 靜默刷新群組，不改變loading狀態，避免頁面跳動
  const silentFetchGroups = async () => {
    try {
      const response = await fetch('/api/groups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取群組失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, groups: data.data.groups || [], error: null }))
    } catch (error: any) {
      console.error('Silent fetch groups failed:', error)
      // 靜默失敗，不顯示錯誤，以免影響用戶體驗
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取使用者列表失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, allUsers: data.data?.users || data.data || [] }))
    } catch (error: any) {
      console.error('獲取使用者列表失敗:', error)
    }
  }

  const fetchAvailableRoles = async () => {
    try {
      const response = await fetch('/api/groups/roles/available', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取可用角色失敗')
      }

      const data = await response.json()
      setState(prev => ({ 
        ...prev, 
        availableRoles: data.data.roles || [],
        loading: false
      }))
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        error: error.message,
        loading: false
      }))
    }
  }

  const handleCreateGroup = async () => {
    try {
      const response = await fetch('/api/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(createForm)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '建立群組失敗')
      }

      showSuccess('權限群組建立成功')
      setCreateForm({ name: '', description: '', color: '#6b7280', roleIds: [] })
      setState(prev => ({ ...prev, showCreateModal: false }))
      await silentFetchGroups()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const handleUpdateGroup = async () => {
    if (!state.editingGroup) return

    try {
      const response = await fetch(`/api/groups/${state.editingGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          name: createForm.name,
          description: createForm.description,
          color: createForm.color,
          roleIds: createForm.roleIds
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '更新群組失敗')
      }

      showSuccess('權限群組更新成功')
      setState(prev => ({ ...prev, showEditModal: false, editingGroup: null }))
      await silentFetchGroups()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const handleDeleteGroup = async () => {
    if (!state.deletingGroupId) return

    try {
      const response = await fetch(`/api/groups/${state.deletingGroupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '刪除群組失敗')
      }

      showSuccess('權限群組刪除成功')
      setState(prev => ({ ...prev, showDeleteModal: false, deletingGroupId: null }))
      await silentFetchGroups()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const handleAssignUser = async (userId: string) => {
    if (!state.assigningToGroupId) return

    try {
      const response = await fetch(`/api/groups/${state.assigningToGroupId}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ userId })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '指派使用者失敗')
      }

      showSuccess('使用者指派成功')
      await silentFetchGroups()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const handleRemoveUser = async (groupId: string, userId: string) => {
    try {
      const response = await fetch(`/api/groups/${groupId}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || '移除使用者失敗')
      }

      showSuccess('使用者移除成功')
      await silentFetchGroups()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const initializeSystemGroups = async () => {
    try {
      const response = await fetch('/api/groups/system/init', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('初始化系統群組失敗')
      }

      showSuccess('系統群組初始化成功')
      await fetchGroups()
    } catch (error: any) {
      showError('操作失敗', error.message)
    }
  }

  const fetchDefaultGroup = async () => {
    try {
      setState(prev => ({ ...prev, defaultGroupLoading: true }))
      const response = await getDefaultGroup()
      
      if (response.success) {
        setState(prev => ({ 
          ...prev, 
          defaultGroup: response.data?.defaultGroup ?? null,
          defaultGroupLoading: false 
        }))
      }
    } catch (error: any) {
      console.error('獲取預設群組失敗:', error)
      setState(prev => ({ ...prev, defaultGroupLoading: false }))
    }
  }

  const handleSetDefaultGroup = async (groupId?: string) => {
    try {
      setState(prev => ({ ...prev, savingDefaultGroup: true }))
      const response = await setDefaultGroup(groupId)
      
      if (response.success) {
        setState(prev => ({ 
          ...prev, 
          defaultGroup: response.data?.defaultGroup ?? null,
          savingDefaultGroup: false 
        }))
        showSuccess(response.message || '預設群組設定成功')
      }
    } catch (error: any) {
      showError('操作失敗', error.message || '預設群組設定失敗')
      setState(prev => ({ ...prev, savingDefaultGroup: false }))
    }
  }

  const openCreateModal = () => {
    setCreateForm({ name: '', description: '', color: '#6b7280', roleIds: [] })
    setState(prev => ({ ...prev, showCreateModal: true }))
  }

  const openEditModal = (group: Group) => {
    setCreateForm({
      name: group.name,
      description: group.description || '',
      color: group.color || '#6b7280',
      roleIds: (group.groupRoles || []).map(gr => gr.role.id)
    })
    setState(prev => ({ ...prev, showEditModal: true, editingGroup: group }))
  }

  // 從群組的角色中提取所有權限
  const getGroupPermissions = (group: Group): string[] => {
    const permissions = new Set<string>()
    group.groupRoles?.forEach(gr => {
      gr.role?.rolePermissions?.forEach(rp => {
        permissions.add(rp.permission)
      })
    })
    return Array.from(permissions)
  }

  const getPermissionLabel = (permission: string): string => {
    // 簡化權限標籤顯示
    return permission.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  }

  const getRoleIcon = (role: string) => {
    switch(role) {
      case 'SUPER_ADMIN': return <ShieldCheckIcon className="w-4 h-4 text-purple-500" />
      case 'ADMIN': return <LockClosedIcon className="w-4 h-4 text-green-500" />
      default: return <EyeIcon className="w-4 h-4 text-blue-500" />
    }
  }

  const getRoleLabel = (role: string): string => {
    switch(role) {
      case 'SUPER_ADMIN': return '超級管理員'
      case 'ADMIN': return '管理員'
      default: return '使用者'
    }
  }

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (state.error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">載入失敗</h3>
        <p className="mt-1 text-sm text-gray-500">{state.error}</p>
        <div className="mt-4 space-x-3">
          <button
            onClick={() => {
              setState(prev => ({ ...prev, loading: true, error: null }))
              Promise.all([fetchGroups(), fetchUsers(), fetchAvailableRoles()])
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            重新載入
          </button>
          <button
            onClick={initializeSystemGroups}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
          >
            初始化系統群組
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <UserGroupIcon className="w-8 h-8 mr-3 text-blue-600" />
                權限群組管理
              </h1>
              <p className="text-base text-gray-600 mt-1">管理自建權限群組和使用者分配</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={initializeSystemGroups}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
              >
                初始化系統群組
              </button>
              <button
                onClick={openCreateModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center text-sm"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                建立群組
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Default Group Setting */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <UserGroupIcon className="w-5 h-5 mr-2 text-green-600" />
            新進會員預設群組設定
          </h2>
          <p className="text-sm text-gray-600 mt-1">設定新註冊使用者自動分配的群組</p>
        </div>
        
        <div className="px-6 py-4">
          {state.defaultGroupLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">載入中...</span>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex-1">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-700">當前預設群組：</span>
                  {state.defaultGroup ? (
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: state.defaultGroup.color }}
                      />
                      <span className="text-sm font-medium text-gray-900">{state.defaultGroup.name}</span>
                      <span className="text-xs text-gray-500">
                        ({state.defaultGroup._count?.users || 0} 位成員)
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 italic">未設定預設群組</span>
                  )}
                </div>
                {state.defaultGroup?.description && (
                  <p className="text-xs text-gray-500 mt-1 ml-20">{state.defaultGroup.description}</p>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                <select
                  className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={state.defaultGroup?.id || ''}
                  onChange={(e) => handleSetDefaultGroup(e.target.value || undefined)}
                  disabled={state.savingDefaultGroup}
                >
                  <option value="">無預設群組</option>
                  {(state.groups || []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group._count.users} 位成員)
                    </option>
                  ))}
                </select>
                
                {state.savingDefaultGroup && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  功能說明
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>設定後，新註冊的使用者將自動加入所選群組</li>
                    <li>適用於一般註冊、Google 登入和 LINE 登入</li>
                    <li>選擇「無預設群組」可清除此設定</li>
                    <li>不影響現有使用者的群組設定</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Groups List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">權限群組列表</h2>
          <p className="text-sm text-gray-600">管理所有權限群組和成員</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {(state.groups || []).map((group) => (
            <div key={group.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                    {group.isSystem && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        系統群組
                      </span>
                    )}
                  </div>
                  {group.description && (
                    <p className="mt-1 text-sm text-gray-600">{group.description}</p>
                  )}
                  
                  <div className="mt-3 flex items-center space-x-6 text-sm text-gray-500">
                    <span className="flex items-center">
                      <UsersIcon className="w-4 h-4 mr-1" />
                      {group._count.users} 位成員
                    </span>
                    <span className="flex items-center">
                      <CogIcon className="w-4 h-4 mr-1" />
                      {getGroupPermissions(group).length} 項權限
                    </span>
                  </div>

                  {/* Members */}
                  {(group.users || []).length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">群組成員:</h4>
                      <div className="flex flex-wrap gap-2">
                        {(group.users || []).map((member) => (
                          <div key={member.id} className="flex items-center bg-gray-100 rounded-lg px-3 py-1">
                            <div className="flex items-center space-x-2">
                              {member.user.avatar ? (
                                <img src={member.user.avatar} alt={member.user.name} className="w-6 h-6 rounded-full" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-600">
                                    {member.user.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm text-gray-700">{member.user.name}</span>
                            </div>
                            {!group.isSystem && (
                              <button
                                onClick={() => handleRemoveUser(group.id, member.user.id)}
                                className="ml-2 text-gray-400 hover:text-red-500"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Roles and Permissions */}
                  {(group.groupRoles || []).length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">群組角色:</h4>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(group.groupRoles || []).map((gr) => (
                          <span key={gr.id} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            {gr.role.name}
                          </span>
                        ))}
                      </div>
                      {getGroupPermissions(group).length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">包含權限:</h4>
                          <div className="flex flex-wrap gap-1">
                            {getGroupPermissions(group).slice(0, 5).map((perm) => (
                              <span key={perm} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {getPermissionLabel(perm)}
                              </span>
                            ))}
                            {getGroupPermissions(group).length > 5 && (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                +{getGroupPermissions(group).length - 5} 更多
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => setState(prev => ({ 
                      ...prev, 
                      showAssignUsersModal: true, 
                      assigningToGroupId: group.id 
                    }))}
                    className="text-blue-600 hover:text-blue-700 p-2"
                    title="指派成員"
                  >
                    <UsersIcon className="w-5 h-5" />
                  </button>
                  
                  {!group.isSystem && (
                    <>
                      <button
                        onClick={() => openEditModal(group)}
                        className="text-gray-600 hover:text-gray-700 p-2"
                        title="編輯群組"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      
                      <button
                        onClick={() => setState(prev => ({ 
                          ...prev, 
                          showDeleteModal: true, 
                          deletingGroupId: group.id 
                        }))}
                        className="text-red-600 hover:text-red-700 p-2"
                        title="刪除群組"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Group Modal */}
      {(state.showCreateModal || state.showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {state.showCreateModal ? '建立權限群組' : '編輯權限群組'}
                </h3>
                <button
                  onClick={() => setState(prev => ({ 
                    ...prev, 
                    showCreateModal: false, 
                    showEditModal: false, 
                    editingGroup: null 
                  }))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    群組名稱
                  </label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="輸入群組名稱"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    群組描述
                  </label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="輸入群組描述（可選）"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    群組顏色
                  </label>
                  <input
                    type="color"
                    value={createForm.color}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, color: e.target.value }))}
                    className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    群組角色
                  </label>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {(state.availableRoles || []).map((role) => (
                      <label key={role.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={createForm.roleIds.includes(role.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCreateForm(prev => ({
                                ...prev,
                                roleIds: [...prev.roleIds, role.id]
                              }))
                            } else {
                              setCreateForm(prev => ({
                                ...prev,
                                roleIds: prev.roleIds.filter(id => id !== role.id)
                              }))
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2" 
                              style={{ backgroundColor: role.color }}
                            />
                            <span className="text-sm font-medium text-gray-900">{role.name}</span>
                          </div>
                          {role.description && (
                            <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {(role.rolePermissions || []).length} 項權限
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setState(prev => ({ 
                    ...prev, 
                    showCreateModal: false, 
                    showEditModal: false, 
                    editingGroup: null 
                  }))}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={state.showCreateModal ? handleCreateGroup : handleUpdateGroup}
                  disabled={!createForm.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {state.showCreateModal ? '建立' : '更新'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {state.showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="text-lg font-medium text-gray-900 mt-4">確認刪除群組</h3>
              <p className="text-sm text-gray-500 mt-2">
                此操作無法復原，群組中的成員將失去相關權限。
              </p>
              <div className="flex justify-center space-x-3 mt-6">
                <button
                  onClick={() => setState(prev => ({ ...prev, showDeleteModal: false, deletingGroupId: null }))}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteGroup}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  確認刪除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Users Modal */}
      {state.showAssignUsersModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">指派成員到群組</h3>
                <button
                  onClick={() => setState(prev => ({ 
                    ...prev, 
                    showAssignUsersModal: false, 
                    assigningToGroupId: null 
                  }))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {(state.allUsers || []).filter(user => user.enabled).map((user) => {
                    const assignedGroup = (state.groups || []).find(g => g.id === state.assigningToGroupId)
                    const isAssigned = assignedGroup?.users?.some(u => u.user.id === user.id)
                    
                    return (
                      <div key={user.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                            <div className="flex items-center mt-1">
                              {getRoleIcon(user.role)}
                              <span className="ml-1 text-xs text-gray-500">{getRoleLabel(user.role)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (isAssigned) {
                              handleRemoveUser(state.assigningToGroupId!, user.id)
                            } else {
                              handleAssignUser(user.id)
                            }
                          }}
                          type="button"
                          className={`px-3 py-1 text-xs font-medium rounded ${
                            isAssigned
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {isAssigned ? '移除' : '加入'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupManagement
